import { captureException } from '@sentry/react';
import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models/neighborhood';
import { adaptV2ServiceArea, adaptV2TopJob } from './adaptV2Neighborhood';
import { fetchV2NeighborhoodPage } from './fetchV2ServiceAreaList';
import { fetchV2ServiceAreaContentPage } from './fetchV2ServiceAreaContentPage';
import {
    V2TopJob,
    V2_EMBEDDED_JOBS_LIMIT,
    V2_STORED_JOBS_LIMIT,
} from './v2NeighborhoodTypes';

type FetchV2ServiceAreaByPostcodeParams = {
    neighborhoodUrl: string;
    serviceAreaContentUrl: string;
    accountId: string;
    postCode: string;
};

/**
 * Distinguishes "v2 answered, and the account does not serve this postcode" from
 * "v2 failed". Both yield no area, but only the second should fall back to v1 —
 * collapsing them would fire a pointless v1 request for every unserved postcode a
 * visitor types.
 */
export type FetchV2ServiceAreaResult = {
    area: NeighborhoodServiceAreaInfo | undefined;
};

/**
 * Fetch one service area by postcode, with as many of its jobs as v2 will serve.
 * Returns undefined only when v2 itself failed.
 *
 * v1's `/serviceareas/neighborhood` returned the area with every job it had (all
 * statuses), and the map plots a pin per job. v2 embeds only the first
 * `V2_EMBEDDED_JOBS_LIMIT` published jobs and pages the rest through
 * `serviceArea/content` up to `V2_STORED_JOBS_LIMIT` (raised to 250 with
 * RFC-NEIGHBORHOOD-MAP-DENSITY); the non-published pins arrive as `densityJobs`
 * and the adapter appends them whole. Only an area with more than 250 *published*
 * jobs still loses pins now — rare — so that residual is not reported per fetch;
 * only a shortfall the cap does not explain is. See the check at the end of the
 * body.
 *
 * Always sends an explicit postcode — omitting it would return every area
 * instead of the one the visitor searched for.
 */
export const fetchV2ServiceAreaByPostcode = async ({
    neighborhoodUrl,
    serviceAreaContentUrl,
    accountId,
    postCode,
}: FetchV2ServiceAreaByPostcodeParams): Promise<
    FetchV2ServiceAreaResult | undefined
> => {
    const response = await fetchV2NeighborhoodPage({
        url: neighborhoodUrl,
        body: {
            accountId,
            page: 1,
            limit: 1,
            postcode: postCode,
        },
    });

    if (!response) {
        return undefined;
    }

    const area = response.serviceAreas?.[0];
    if (!area) {
        // A postcode the account does not serve. v1 returned undefined here and
        // MapProvider renders its NOT_FOUND state from that, so this is a normal
        // answer — not a failure, and not something to retry against v1.
        return { area: undefined };
    }

    const adapted = adaptV2ServiceArea(area);

    if (!area.hasMoreJobs) {
        return { area: adapted };
    }

    // Page from 2 at the embed-matching limit: page 1 at this limit *is* the
    // embedded window, so starting at 2 cannot duplicate or skip a job.
    const extraJobs: V2TopJob[] = [];
    const maxPage = Math.ceil(V2_STORED_JOBS_LIMIT / V2_EMBEDDED_JOBS_LIMIT);
    let page = 2;
    let hasMore = true;
    let pageFailed = false;

    while (hasMore && page <= maxPage) {
        const contentPage = await fetchV2ServiceAreaContentPage({
            url: serviceAreaContentUrl,
            body: {
                accountId,
                postcode: area.postcode,
                type: 'jobs',
                page,
                limit: V2_EMBEDDED_JOBS_LIMIT,
            },
        });

        if (!contentPage) {
            // Return what we have rather than dropping the area entirely — a map
            // with the embedded jobs beats no map. Already reported above.
            pageFailed = true;
            break;
        }

        extraJobs.push(...(contentPage.data ?? []));
        hasMore = contentPage.hasMore;
        page += 1;
    }

    // `adapted.jobs` already carries the embedded published jobs plus the
    // anonymized density pins; append the paged published jobs to it for the map.
    const jobs = [...adapted.jobs, ...extraJobs.map(adaptV2TopJob)];

    // The shortfall check is about *published* jobs only, so it counts the raw
    // published jobs served (embedded + paged), not `jobs.length` — the density
    // pins folded in by the adapter would otherwise mask a real shortfall against
    // the published-only `area.jobCount`.
    const publishedServed = area.jobs.length + extraJobs.length;

    // Only report a shortfall we cannot already explain. Why the walk stopped is
    // the signal, not the raw count:
    //
    // - `pageFailed`: fetchV2ServiceAreaContentPage already reported it.
    // - `hasMore` still true: the walk hit the depth cap. Expected and known —
    //   the aggregate stores at most V2_STORED_JOBS_LIMIT published jobs per
    //   area; at 250 real accounts rarely reach it, and any that do are the known
    //   cap tracked in RFC-NEIGHBORHOOD-MAP-DENSITY. Reporting these would fire on
    //   every visitor search of such a zip, burying the failures worth seeing.
    // - `hasMore` false but still short: the service said it had nothing more
    //   while claiming a larger jobCount. Nothing explains that, so it is worth
    //   knowing about.
    if (!pageFailed && !hasMore && area.jobCount > publishedServed) {
        captureException(
            new Error(
                `V2 service area ${area.postcode} served ${publishedServed} of ${area.jobCount} published jobs ` +
                    `and reported no more pages, below the ${V2_STORED_JOBS_LIMIT}-job cap`
            ),
            { extra: { accountId, postcode: area.postcode } }
        );
    }

    return { area: { ...adapted, jobs } };
};
